// The controller's tuning, in the terms a designer tunes and the harness measures (decision 0044: "the harness
// measures time to top speed, stopping distance and turn time"): time to 90 % speed rather than an acceleration rate,
// a stopping distance rather than a damping, a turn time, a jump height and rise time rather than gravity and a
// take-off speed. `deriveRates` turns them into the rates the controller steps with, exactly for a fixed step, so a
// feel gate measured on the running game lands on the table's number. Every value is the game's (a content table);
// the package holds none. Speeds in m/s, times in s, distances in m, angles in degrees.

/** Everything the controller needs to know, as the game's tuning table states it. */
export interface ControllerTuning {
  /** The capsule: radius and the half length of its straight part (m). Standing height = 2 × (halfHeight + radius). */
  radius: number;
  halfHeight: number;
  /** The gap kept from the world (m). */
  skin: number;

  /** Top speeds on flat ground. */
  walkSpeed: number;
  runSpeed: number;
  sprintSpeed: number;
  /** Stick tilts (0..1): under `deadZone` no input; at `walkTilt` walk speed; from `runTilt` run speed; a ramp between. */
  deadZone: number;
  walkTilt: number;
  runTilt: number;
  /** Rest to 90 % of the target speed (s). */
  accelTime: number;
  /** Travel after letting go of the stick at run speed (m). */
  stopDistance: number;
  /** A 90° stick turn at a run: until facing is within 10° of the new heading (s). */
  turnTime: number;

  /** A stick further than this off the velocity at speed brakes first (a skid) instead of carving round (degrees). */
  skidAngle: number;
  /** The skid's braking rate (1/s, exponential). */
  skidDecel: number;
  /** Under this speed a big stick turn pivots in place first (m/s). */
  pivotSpeed: number;
  /** A stick at least this far off the facing from (near) rest pivots in place (degrees). */
  pivotAngle: number;
  /** The pivot ends, and the body sets off, when facing is within this of the stick (degrees). */
  pivotRelease: number;
  /** The pivot's turn speed (degrees/s). */
  pivotRate: number;

  /** Sprint start: a burst lasting `burstTime`, peaking at `burstBoost` over sprint speed, approached at `burstAccel`. */
  burstTime: number;
  burstBoost: number;
  burstAccel: number;

  /** The steepest slope walked up (degrees). */
  maxSlope: number;
  /** The tallest step climbed without a jump (m), and the least depth of its top (m). */
  stepHeight: number;
  stepMinWidth: number;
  /** Stay on the ground when it falls away by up to this (m): down slopes and steps. */
  snapDistance: number;

  /** A held jump's apex height (m) and take-off to apex (s). Gravity and the take-off speed follow from these. */
  jumpHeight: number;
  jumpRiseTime: number;
  /** Gravity × this while falling: a faster fall than rise reads as weight. */
  fallGravityScale: number;
  /** Gravity × this while rising with the jump let go: variable jump height. */
  jumpCutGravityScale: number;
  /** Terminal fall speed (m/s). */
  maxFallSpeed: number;
  /** After walking off a ledge, a jump still works for this long (s). */
  coyoteTime: number;
  /** A jump pressed up to this long before landing jumps on landing (s). */
  jumpBuffer: number;
  /** How fast the stick changes the horizontal velocity in the air (m/s²), the facing's turn speed there (degrees/s),
   *  and the bleed with no stick (m/s²). */
  airAccel: number;
  airTurnRate: number;
  airDrag: number;

  /** The glide: steady sink speed (m/s); horizontal speed with the stick full and with none (m/s); how fast the
   *  horizontal velocity changes (m/s²); the facing's turn speed (degrees/s); how fast a fall is braked to the sink
   *  (1/s); the least height above ground to open it (m). */
  glideSinkSpeed: number;
  glideSpeed: number;
  glideIdleSpeed: number;
  glideAccel: number;
  glideTurnRate: number;
  glideBrake: number;
  glideMinHeight: number;

  /** Wading: speed × `wadeSpeedScale` once the feet are `wadeDepth` under water (eased from dry land). From
   *  `swimDepth` a swim mode takes over, when the game has one. */
  wadeSpeedScale: number;
  wadeDepth: number;
  swimDepth: number;
}

/** The rates the controller steps with, derived from the tuning for one fixed step. */
export interface ControllerRates {
  dt: number;
  /** Exponential approach rate to a higher target speed (1/s). */
  accel: number;
  /** Exponential decay rate of speed with no stick (1/s), so a stop from run speed travels `stopDistance`. */
  decel: number;
  /** The carve's turn speed (rad/s). */
  turn: number;
  pivotRate: number;
  skidAngle: number;
  pivotAngle: number;
  pivotRelease: number;
  airTurn: number;
  glideTurn: number;
  /** Gravity (m/s², positive down) and the jump's take-off speed (m/s). */
  gravity: number;
  jumpSpeed: number;
  maxSlope: number;
}

const DEG = Math.PI / 180;

/**
 * The step rates for a tuning at a fixed step `dt`. Speed approaches its target as v(n) = T(1 − qⁿ) with
 * q = e^(−accel·dt), which reaches 90 % at exactly `accelTime`. A stop multiplies speed by q each step before moving,
 * travelling v₀·dt·q/(1 − q); solving that for `stopDistance` from run speed gives the decay rate. The carve turns at a
 * constant speed, 80° in `turnTime`. The jump uses constant-gravity steps (exact), so the apex is `jumpHeight` at
 * `jumpRiseTime`.
 */
export function deriveRates(t: ControllerTuning, dt: number): ControllerRates {
  const q = t.stopDistance / (t.stopDistance + t.runSpeed * dt);
  return {
    dt,
    accel: Math.log(10) / t.accelTime,
    decel: -Math.log(q) / dt,
    turn: (80 * DEG) / t.turnTime,
    pivotRate: t.pivotRate * DEG,
    skidAngle: t.skidAngle * DEG,
    pivotAngle: t.pivotAngle * DEG,
    pivotRelease: t.pivotRelease * DEG,
    airTurn: t.airTurnRate * DEG,
    glideTurn: t.glideTurnRate * DEG,
    gravity: (2 * t.jumpHeight) / (t.jumpRiseTime * t.jumpRiseTime),
    jumpSpeed: (2 * t.jumpHeight) / t.jumpRiseTime,
    maxSlope: t.maxSlope * DEG,
  };
}

/** Problems with a tuning (empty when sound): missing or non-finite values, and orders that make no sense. */
export function checkTuning(t: ControllerTuning): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(t)) if (typeof v !== "number" || !Number.isFinite(v)) out.push(`${k} is not a finite number`);
  if (out.length) return out;
  const pos: (keyof ControllerTuning)[] = ["radius", "walkSpeed", "runSpeed", "sprintSpeed", "accelTime", "stopDistance", "turnTime", "jumpHeight", "jumpRiseTime", "maxFallSpeed", "glideSinkSpeed", "glideSpeed", "glideAccel", "glideBrake", "pivotRate"];
  for (const k of pos) if (!(t[k] > 0)) out.push(`${k} must be > 0`);
  if (!(t.walkSpeed <= t.runSpeed && t.runSpeed <= t.sprintSpeed)) out.push("speeds must rise walk ≤ run ≤ sprint");
  if (!(t.deadZone >= 0 && t.deadZone < t.walkTilt && t.walkTilt < t.runTilt && t.runTilt <= 1)) out.push("tilts must rise 0 ≤ deadZone < walkTilt < runTilt ≤ 1");
  if (!(t.maxSlope > 0 && t.maxSlope < 90)) out.push("maxSlope must be between 0 and 90 degrees");
  if (!(t.snapDistance >= t.stepHeight)) out.push("snapDistance should be at least stepHeight, or walking down a step leaves the ground");
  if (!(t.pivotRelease < t.pivotAngle)) out.push("pivotRelease must be under pivotAngle");
  return out;
}

/** The target speed for a stick tilt (0..1) on flat ground: a ramp from walk to run, sprint when held. */
export function speedForTilt(t: ControllerTuning, tilt: number, sprint: boolean): number {
  if (tilt <= t.deadZone) return 0;
  if (sprint) return t.sprintSpeed;
  if (tilt <= t.walkTilt) return t.walkSpeed * ((tilt - t.deadZone) / (t.walkTilt - t.deadZone));
  if (tilt >= t.runTilt) return t.runSpeed;
  return t.walkSpeed + (t.runSpeed - t.walkSpeed) * ((tilt - t.walkTilt) / (t.runTilt - t.walkTilt));
}
